import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { dataCache, CACHE_DURATIONS } from '../utils/dataCache';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import PullToRefresh from '../components/PullToRefresh';
import SyncButton from '../components/SyncButton';
import SkeletonLoader from '../components/SkeletonLoader';

interface Lesson {
  id: string;
  title: string;
  chapter_id: string;
  order_index: number;
  Chapters: {
    name: string;
    order_index: number;
    CourseLevels: {
      level_code: string;
      level_name: string;
      order_index: number;
    };
  };
}

interface QuizProgress {
  lessonId: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  errors: number;
  completed: boolean;
}

const QuizListScreen: React.FC = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [quizProgress, setQuizProgress] = useState<{ [key: string]: QuizProgress }>({});
  const [questionCounts, setQuestionCounts] = useState<{ [key: string]: number }>({});
  const [currentLanguage, setCurrentLanguage] = useState<'bn' | 'it' | 'en'>('bn');
  const [loading, setLoading] = useState(false); // Start false - show cached data immediately
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Language content
  const content = {
    bn: {
      title: "কুইজ ও পরীক্ষা",
      quiz: "কুইজ",
      minutes: "মিনিট",
      questions: "প্রশ্ন",
      completed: "% সম্পূর্ণ",
      yourScore: "আপনার স্কোর"
    },
    it: {
      title: "Quiz e Test",
      quiz: "Quiz",
      minutes: "minuti",
      questions: "domande",
      completed: "% completato",
      yourScore: "Il tuo punteggio"
    },
    en: {
      title: "Quiz and Test",
      quiz: "Quiz",
      minutes: "minutes",
      questions: "questions",
      completed: "% Completed",
      yourScore: "Your Score"
    }
  };

  useEffect(() => {
    // Load saved language preference first (synchronous)
    const savedLanguage = localStorage.getItem('preferredLanguage') as 'bn' | 'it' | 'en';
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Load progress from localStorage first (instant)
    loadQuizProgress();

    // Try to load from cache first for instant display
    const cachedLessons = dataCache.get<Lesson[]>('quiz_lessons');
    const cachedQuestionCounts = dataCache.get<{ [key: string]: number }>('quiz_question_counts');
    
    if (cachedLessons && cachedLessons.length > 0) {
      setLessons(cachedLessons);
      setLoading(false);
    }
    
    if (cachedQuestionCounts) {
      setQuestionCounts(cachedQuestionCounts);
    }
    
    // Load fresh data in background (non-blocking)
    setTimeout(() => {
      loadLessons(!!(cachedLessons && cachedLessons.length > 0));
      loadQuestionCounts(!!cachedQuestionCounts);
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLessons = async (forceRefresh: boolean = false) => {
    try {
      // Only show loading if we don't have cached data
      if (!forceRefresh && lessons.length === 0) {
        setLoading(true);
      }
      
      const cacheKey = 'quiz_lessons';
      
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedLessons = dataCache.get<Lesson[]>(cacheKey);
        if (cachedLessons && cachedLessons.length > 0) {
          setLessons(cachedLessons);
          setLoading(false);
          // Refresh in background (non-blocking)
          setTimeout(() => loadLessons(true), 100);
          return;
        }
      }
      
      const { data, error } = await supabase
        .from('Lessons')
        .select(`
          *,
          Chapters!inner(
            name,
            order_index,
            CourseLevels!inner(
              level_code,
              level_name,
              order_index
            )
          )
        `);

      if (error) throw error;
      
      // Sort lessons by Level → Chapter → Lesson order
      const sortedLessons = (data || []).sort((a, b) => {
        const aChapter = Array.isArray(a.Chapters) ? a.Chapters[0] : a.Chapters;
        const bChapter = Array.isArray(b.Chapters) ? b.Chapters[0] : b.Chapters;
        
        const aLevel = Array.isArray(aChapter?.CourseLevels) ? aChapter.CourseLevels[0] : aChapter?.CourseLevels;
        const bLevel = Array.isArray(bChapter?.CourseLevels) ? bChapter.CourseLevels[0] : bChapter?.CourseLevels;
        
        // First sort by level order_index
        if (aLevel?.order_index !== bLevel?.order_index) {
          return (aLevel?.order_index || 0) - (bLevel?.order_index || 0);
        }
        
        // Then by chapter order_index
        if (aChapter?.order_index !== bChapter?.order_index) {
          return (aChapter?.order_index || 0) - (bChapter?.order_index || 0);
        }
        
        // Finally by lesson order_index
        return (a.order_index || 0) - (b.order_index || 0);
      });
      
      // If refresh returns empty and we have existing data, preserve existing data
      if (sortedLessons.length === 0 && lessons.length > 0) {
        console.warn('No lessons data returned from refresh, keeping existing data');
        setLoading(false);
        return;
      }

      // Only update if we have data OR this is initial load (to show proper empty state)
      if (sortedLessons.length === 0 && lessons.length === 0) {
        // Initial load with no data - show empty state
        setLessons([]);
        setLoading(false);
        return;
      }

      // Smooth update - only update if data actually changed
      setLessons(prevLessons => {
        // If data is the same, don't update to prevent flicker
        if (prevLessons.length === sortedLessons.length && 
            prevLessons.every((prev, idx) => prev.id === sortedLessons[idx]?.id)) {
          return prevLessons; // Keep existing to prevent re-render
        }
        return sortedLessons;
      });
      
      // Cache the data (only if we have data)
      if (sortedLessons.length > 0) {
        dataCache.set(cacheKey, sortedLessons, CACHE_DURATIONS.LESSONS);
      }
    } catch (error) {
      console.error('Error loading lessons:', error);
      // On error, keep existing data visible - don't clear
      if (lessons.length === 0) {
        // Only set empty if we had no data to begin with
        setLessons([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQuizProgress = () => {
    try {
      const progressKey = 'quizProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {};
      setQuizProgress(progress);
    } catch (error) {
      console.error('Error loading quiz progress:', error);
    }
  };

  const loadQuestionCounts = async (forceRefresh: boolean = false) => {
    try {
      const cacheKey = 'quiz_question_counts';
      
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedCounts = dataCache.get<{ [key: string]: number }>(cacheKey);
        if (cachedCounts) {
          setQuestionCounts(cachedCounts);
          // Refresh in background
          setTimeout(() => loadQuestionCounts(true), 100);
          return;
        }
      }
      
      // Single optimized query to get question counts for all lessons
      const { data: questions, error: questionsError } = await supabase
        .from('Questions')
        .select('lesson_id');

      if (questionsError) throw questionsError;

      // Count questions per lesson efficiently
      const counts: { [key: string]: number } = {};
      (questions || []).forEach(question => {
        counts[question.lesson_id] = (counts[question.lesson_id] || 0) + 1;
      });

      setQuestionCounts(counts);
      // Cache the data
      dataCache.set(cacheKey, counts, CACHE_DURATIONS.LESSONS);
    } catch (error) {
      console.error('Error loading question counts:', error);
      // On error, keep existing data visible
    }
  };

  const handleQuizSelect = (lessonId: string) => {
    navigate(`/quiz/${lessonId}`);
  };

  const getProgressData = (lessonId: string) => {
    const progress = quizProgress[lessonId];
    if (!progress) {
      return { percentage: 0, score: 0, errors: 0, completed: false, totalQuestions: 0 };
    }
    return progress;
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Don't clear cache - just force refresh to get latest data
      // This way if refresh fails, we still have cached data visible
      await Promise.all([
        loadLessons(true),
        loadQuestionCounts(true),
      ]);
    } catch (error) {
      console.error('Error refreshing quizzes:', error);
      // Keep existing data visible on error
    } finally {
      setIsRefreshing(false);
  }
  };

  return (
    <div className="quiz-list-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content[currentLanguage].title}</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <SyncButton 
            onSync={handleRefresh} 
            isSyncing={isRefreshing}
            size="small"
          />
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
        </div>
      </div>

      <LessonBannerAd />

      <PullToRefresh onRefresh={handleRefresh} disabled={isRefreshing}>
      <div className="quiz-list-container">
        {loading && lessons.length === 0 ? (
          <div className="no-quizzes">
            <SkeletonLoader type="card" count={3} />
          </div>
        ) : lessons.length === 0 ? (
          <div className="no-quizzes">
            <p>No quizzes available yet.</p>
          </div>
        ) : (
          (() => {
            // Filter lessons that have at least 1 question
            const lessonsWithQuizzes = lessons.filter(lesson => 
              questionCounts[lesson.id] && questionCounts[lesson.id] > 0
            );
            
            if (lessonsWithQuizzes.length === 0) {
              return (
                <div className="no-quizzes">
                  <p>No quizzes available yet.</p>
                </div>
              );
            }
            
            let lastChapterId = '';
            let lessonNumberInChapter = 0;
            let colorIndex = 0;
            
            return lessonsWithQuizzes.map((lesson, index) => {
              const progress = getProgressData(lesson.id);
              const chapterData = Array.isArray(lesson.Chapters) ? lesson.Chapters[0] : lesson.Chapters;
              const levelData = chapterData?.CourseLevels;
              const level = Array.isArray(levelData) ? levelData[0] : levelData;
              
              // Reset lesson number when chapter changes
              if (lesson.chapter_id !== lastChapterId) {
                lastChapterId = lesson.chapter_id;
                lessonNumberInChapter = 1;
              } else {
                lessonNumberInChapter++;
              }
              
              // Define standard modern app colors
              const colors = ['#ff6b35', '#4caf50', '#9c27b0', '#2196F3', '#FF9800'];
              const color = colors[colorIndex % colors.length];
              colorIndex++;
              
              // Show chapter header for first lesson in chapter
              const isFirstInChapter = index === 0 || lessonsWithQuizzes[index - 1].chapter_id !== lesson.chapter_id;

              return (
                <React.Fragment key={lesson.id}>
                  {isFirstInChapter && (
                    <div className="chapter-header-quiz">
                      <div className="level-badge">{level?.level_code || 'A0'}</div>
                      <h2 className="chapter-name">{chapterData?.name || 'Chapter'}</h2>
                    </div>
                  )}
                  
                  <div 
                    className="quiz-card"
                    style={{ backgroundColor: color }}
                    onClick={() => handleQuizSelect(lesson.id)}
                  >
                    <div className="wrap-quiz-icon">
                      <div className="quiz-icon">
                        <div className="icon-placeholder">{lessonNumberInChapter}</div>
                      </div>
                      <div className="quiz-content">
                        <div className="quiz-label">{content[currentLanguage].quiz}</div>
                        <h3 className="quiz-title">{lesson.title}</h3>
                      
                        <div className="quiz-tags">
                          <span className="tag">{Math.ceil(questionCounts[lesson.id] * 1.5)} {content[currentLanguage].minutes}</span>
                          <span className="tag">{questionCounts[lesson.id]} {content[currentLanguage].questions}</span>
                        </div>
                        
                        <div className="progress-section">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${progress.percentage}%` }}
                            ></div>
                          </div>
                          <div className="progress-text">{progress.percentage}{content[currentLanguage].completed}</div>
                        </div>
                      </div>
                    </div>

                    <div className="score-box">
                      <div className="score-number">
                        {progress.completed ? `${progress.score}/${progress.totalQuestions}` : `0/${questionCounts[lesson.id]}`}
                      </div>
                      <div className="score-label">{content[currentLanguage].yourScore}</div>
                    </div>
                  </div>
                </React.Fragment>
              );
            });
          })()
        )}
      </div>
      </PullToRefresh>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default QuizListScreen;

